import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, Lock } from "lucide-react";
import { EASE } from "@/components/Reveal";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatApiErrorDetail = (detail) => {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
};

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      localStorage.setItem("hr99_token", data.token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: EASE }}
        className="w-full max-w-md"
      >
        <a href="/" className="inline-flex items-center gap-4 mb-10" data-testid="admin-login-logo">
          <Logo />
          <span className="text-xs uppercase tracking-[0.25em] text-zinc-500">Admin</span>
        </a>
        <form onSubmit={submit} className="bg-zinc-950/60 border border-white/10 p-8 md:p-10 space-y-5" data-testid="admin-login-form">
          <div className="flex items-center gap-3 mb-2">
            <Lock className="w-5 h-5 text-primary" />
            <h1 className="font-heading text-2xl font-medium text-white">Team Login</h1>
          </div>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="Admin email"
            className="w-full bg-zinc-900 border border-zinc-800 px-5 py-4 text-white placeholder:text-zinc-600 text-sm focus:outline-none focus:border-primary transition-colors"
            data-testid="admin-email-input"
          />
          <input
            type="password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Password"
            className="w-full bg-zinc-900 border border-zinc-800 px-5 py-4 text-white placeholder:text-zinc-600 text-sm focus:outline-none focus:border-primary transition-colors"
            data-testid="admin-password-input"
          />
          {error && (
            <p className="text-sm text-red-400" data-testid="admin-login-error">{error}</p>
          )}
          <button
            type="submit"
            disabled={loading}
            className="w-full inline-flex items-center justify-center gap-3 bg-primary text-primary-foreground px-8 py-4 font-semibold hover:bg-orange-400 transition-colors disabled:opacity-60"
            data-testid="admin-login-submit"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
          </button>
        </form>
      </motion.div>
    </div>
  );
}
